import { 
  TAX_BRACKETS_BY_YEAR, 
  MEDICARE_LEVY_RATE, 
  MEDICARE_LEVY_SURCHARGE,
  HELP_REPAYMENT_THRESHOLDS,
  SUPER_RATES_BY_YEAR,
  TAX_BRACKETS,
  DIVISION_293_THRESHOLD,
  DEPENDANT_CHILDREN_OFFSET,
  SPOUSE_REBATE,
  SENIORS_PENSIONER_OFFSET,
  CONCESSIONAL_CONTRIBUTIONS_CAP
} from './constants';
import { CalculatorValues, TaxResults, IncomeInputType, TaxYearType } from './types';
import { round } from './utils';
import { calculateEstimatedTaxReturn } from './payg';

// Use the imported constant for concessional cap to avoid inconsistencies
const BASE_CONCESSIONAL_CAP = CONCESSIONAL_CONTRIBUTIONS_CAP;

/**
 * Convert income from any input type to annual amount
 */
export function convertToAnnualIncome(income: number, inputType: IncomeInputType, hoursPerWeek: number): number {
  switch (inputType) {
    case "hourly":
      return income * hoursPerWeek * 52;
    case "weekly":
      return income * 52;
    case "monthly":
      return income * 12;
    case "annually":
    default:
      return income;
  }
}

/**
 * Calculate income tax based on the provided income
 * Using Australian tax brackets for the selected year
 */
export function calculateIncomeTax(
  taxableIncome: number, 
  taxYear: TaxYearType = "2023-2024", 
  isResident: boolean = true, 
  claimTaxFreeThreshold: boolean = true
): number {
  // For non-residents, different tax rates apply
  if (!isResident) {
    // Non-resident tax rates for 2023-24
    if (taxableIncome <= 120000) {
      return taxableIncome * 0.325;
    } else if (taxableIncome <= 180000) {
      return 39000 + (taxableIncome - 120000) * 0.37;
    } else {
      return 61200 + (taxableIncome - 180000) * 0.45;
    }
  }
  
  // For residents
  // If not claiming the tax-free threshold, adjust the calculation
  if (!claimTaxFreeThreshold) {
    // When not claiming tax-free threshold, different withholding rates apply
    // This is a simplified approximation
    return taxableIncome * 0.19; // Higher withholding rate
  }
  
  // Standard resident tax calculation with tax-free threshold
  const taxBrackets = TAX_BRACKETS_BY_YEAR[taxYear] || TAX_BRACKETS;
  
  for (const bracket of taxBrackets) {
    if (taxableIncome <= bracket.max) {
      return bracket.base + (taxableIncome - bracket.min) * bracket.rate;
    }
  }
  
  // Fallback (shouldn't reach here with proper brackets)
  return 0;
}

/**
 * Calculate Medicare Levy (2% of taxable income for most taxpayers)
 * 
 * Based on ATO guidelines: https://www.ato.gov.au/individuals-and-families/medicare-and-private-health-insurance/medicare-levy/medicare-levy-exemption/medical-exemption-from-medicare-levy
 */
export function calculateMedicareLevy(taxableIncome: number, medicareExempt: "none" | "half" | "full" = "none"): number {
  if (medicareExempt === "full") {
    // Full Medicare levy exemption (0%)
    return 0;
  } else if (medicareExempt === "half") {
    // Half Medicare levy exemption (1%)
    return taxableIncome * (MEDICARE_LEVY_RATE / 2);
  } else {
    // No exemption (standard 2%)
    return taxableIncome * MEDICARE_LEVY_RATE;
  }
}

/**
 * Calculate Medicare Levy Surcharge for those without private health insurance
 */
export function calculateMedicareLevySurcharge(taxableIncome: number, hasPrivateHealth: boolean): number {
  if (hasPrivateHealth) return 0;
  
  for (const tier of MEDICARE_LEVY_SURCHARGE) {
    if (taxableIncome >= tier.min && taxableIncome <= tier.max) {
      return taxableIncome * tier.rate;
    }
  }
  
  return 0;
}

/**
 * Calculate HELP/HECS repayments
 */
export function calculateHELPRepayment(taxableIncome: number, hasHELP: boolean): number {
  if (!hasHELP) return 0;
  
  for (const threshold of HELP_REPAYMENT_THRESHOLDS) {
    if (taxableIncome >= threshold.min && taxableIncome <= threshold.max) {
      return taxableIncome * threshold.rate;
    }
  }
  
  return 0;
}

/**
 * Calculate Division 293 tax (additional 15% tax on superannuation contributions for high income earners)
 * 
 * Based on ATO rules: https://www.ato.gov.au/individuals-and-families/super-for-individuals-and-families/super/growing-and-keeping-track-of-your-super/caps-limits-and-tax-on-super-contributions/division-293-tax-on-concessional-contributions-by-high-income-earners
 */
export function calculateDivision293Tax(income: number, superContributions: number): number {
  // Division 293 tax applies when income > threshold
  if (income > DIVISION_293_THRESHOLD) {
    // Division 293 tax is 15% of the lesser of:
    // 1. Concessional contributions (super contributions)
    // 2. The amount over the Division 293 threshold (the income excess)
    const incomeOverThreshold = income - DIVISION_293_THRESHOLD;
    const taxableAmount = Math.min(superContributions, incomeOverThreshold);
    
    // Division 293 tax is 15% of the taxable amount
    return taxableAmount * 0.15;
  }
  
  // If combined income (income + super) > threshold but income <= threshold
  const combinedIncome = income + superContributions;
  if (combinedIncome > DIVISION_293_THRESHOLD) {
    // The taxable amount is the amount that made the combined income go over the threshold
    const taxableAmount = combinedIncome - DIVISION_293_THRESHOLD;
    
    // Division 293 tax is 15% of the taxable amount
    return taxableAmount * 0.15;
  }
  
  return 0;
}

/**
 * Calculate superannuation (for employees)
 */
export function calculateSuperannuation(totalIncome: number, employmentType: string, superRate: number): number {
  if (employmentType === 'employee') {
    // Super is paid on all income for employees
    return totalIncome * superRate;
  }
  
  // ABN holders don't get automatic super
  return 0;
}

/**
 * Calculate marginal tax rate based on income
 */
export function calculateMarginalTaxRate(
  taxableIncome: number, 
  taxYear: TaxYearType = "2023-2024", 
  isResident: boolean = true
): number {
  if (!isResident) {
    if (taxableIncome <= 120000) return 32.5;
    if (taxableIncome <= 180000) return 37;
    return 45;
  }
  
  const taxBrackets = TAX_BRACKETS_BY_YEAR[taxYear] || TAX_BRACKETS;
  
  for (const bracket of taxBrackets) {
    if (taxableIncome <= bracket.max) {
      return bracket.rate * 100;
    }
  }
  
  // Default to highest bracket
  return 45;
}

/**
 * Calculate dependant children tax offset
 * Based on ATO rules: https://www.ato.gov.au/individuals-and-families/medicare-and-private-health-insurance/medicare-levy/medicare-levy-exemption/dependants-for-medicare-levy-exemption
 */
export function calculateDependantChildrenOffset(
  taxableIncome: number,
  numberOfChildren: number
): number {
  if (numberOfChildren === 0 || taxableIncome >= DEPENDANT_CHILDREN_OFFSET.MAX_INCOME) {
    return 0;
  }

  // Calculate base offset amount
  let offset = DEPENDANT_CHILDREN_OFFSET.BASE_AMOUNT;
  if (numberOfChildren > 1) {
    offset += (numberOfChildren - 1) * DEPENDANT_CHILDREN_OFFSET.ADDITIONAL_AMOUNT;
  }

  // Apply phase out if income is over threshold
  if (taxableIncome > DEPENDANT_CHILDREN_OFFSET.MAX_INCOME) {
    const excess = taxableIncome - DEPENDANT_CHILDREN_OFFSET.MAX_INCOME;
    const reduction = excess * DEPENDANT_CHILDREN_OFFSET.PHASE_OUT_RATE;
    offset = Math.max(0, offset - reduction);
  }

  return round(offset);
}

/**
 * Calculate spouse rebate
 * Based on ATO rules: https://www.ato.gov.au/individuals-and-families/your-tax-return/instructions-to-complete-your-tax-return/paper-tax-return-instructions/2024/supporting-information/rebate-income-2024
 */
export function calculateSpouseRebate(
  spouseIncome: number | null | undefined
): number {
  // Handle undefined or null values
  if (spouseIncome === null || spouseIncome === undefined || isNaN(spouseIncome)) {
    return 0;
  }
  
  if (spouseIncome >= SPOUSE_REBATE.MAX_INCOME) {
    return 0;
  }

  // Calculate rebate based on spouse's income
  if (spouseIncome <= SPOUSE_REBATE.THRESHOLD) {
    return SPOUSE_REBATE.BASE_AMOUNT;
  }

  // Apply phase out
  const excess = spouseIncome - SPOUSE_REBATE.THRESHOLD;
  const reduction = excess * SPOUSE_REBATE.PHASE_OUT_RATE;
  return round(Math.max(0, SPOUSE_REBATE.BASE_AMOUNT - reduction));
}

/**
 * Calculate seniors and pensioner tax offset
 * Based on ATO rules: https://www.ato.gov.au/calculators-and-tools/tax-offsets-beneficiaries-and-seniors-and-pensioners-calculator
 */
export function calculateSeniorsPensionerOffset(
  taxableIncome: number,
  isSingle: boolean = true
): number {
  const offsetConfig = isSingle ? 
    SENIORS_PENSIONER_OFFSET.SINGLE : 
    SENIORS_PENSIONER_OFFSET.COUPLE;

  if (taxableIncome <= offsetConfig.THRESHOLD) {
    return offsetConfig.BASE_AMOUNT;
  }

  // Apply phase out
  const excess = taxableIncome - offsetConfig.THRESHOLD;
  const reduction = excess * offsetConfig.PHASE_OUT_RATE;
  return round(Math.max(0, offsetConfig.BASE_AMOUNT - reduction));
}

/**
 * Main function to calculate all tax components
 */
export function calculateTaxResults(values: CalculatorValues): TaxResults {
  const { 
    income, 
    bonusIncome,
    employmentType, 
    isResident, 
    claimTaxFreeThreshold, 
    hasHELP, 
    hasPrivateHealth,
    superRate,
    deductions,
    // Medicare fields
    medicareExempt,
    // Super fields
    salarySacrifice,
    salarySacrificeFrequency,
    salaryIncludesSuper,
    additionalSuper,
    voluntarySuper,
    carryForwardSuper,
    // Tax offset fields
    incomeLevies,
    taxOffsets,
    dependantChildren,
    hasSpouseRebate,
    spouseRebate,
    seniorsPensionerOffset,
    // Other fields
    taxYear,
    incomeInputType,
    hoursPerWeek
  } = values;
  
  // Convert income to annual amount based on the input type
  const annualIncome = convertToAnnualIncome(income, incomeInputType, hoursPerWeek);
  
  // Convert salary sacrifice to annual amount if needed
  let annualSalarySacrifice = salarySacrifice;
  if (salarySacrificeFrequency === "weekly") {
    annualSalarySacrifice = salarySacrifice * 52;
  } else if (salarySacrificeFrequency === "fortnightly") {
    annualSalarySacrifice = salarySacrifice * 26;
  } else if (salarySacrificeFrequency === "monthly") {
    annualSalarySacrifice = salarySacrifice * 12;
  }
  
  // Get the super rate for the selected tax year
  const effectiveSuperRate = superRate || SUPER_RATES_BY_YEAR[taxYear];
  
  // Calculate the actual income amount based on whether salary includes super
  let actualIncome = annualIncome;
  if (employmentType === "employee" && salaryIncludesSuper) {
    // If salary includes super, we need to calculate the actual income (without super)
    actualIncome = annualIncome / (1 + effectiveSuperRate);
  }
  
  // Handle bonus income
  let actualBonusIncome = bonusIncome || 0;
  if (employmentType === "employee" && salaryIncludesSuper && bonusIncome > 0) {
    // If salary includes super, bonus should also include super
    actualBonusIncome = bonusIncome / (1 + effectiveSuperRate);
  }
  
  // Add bonus income to the actual income
  const totalIncome = actualIncome + actualBonusIncome;
  
  // Apply salary sacrifice (reduces taxable income)
  const incomeAfterSalarySacrifice = Math.max(0, totalIncome - annualSalarySacrifice - voluntarySuper);

  // Calculate taxable income (gross income minus deductions)
  // Note: voluntarySuper shouldn't be subtracted here as it's already handled through the super cap calculations
  const taxableIncome = Math.max(0, incomeAfterSalarySacrifice - deductions);
  
  // Calculate tax offsets
  const dependantChildrenOffset = calculateDependantChildrenOffset(taxableIncome, values.dependantChildren);
  // Calculate spouse rebate amount only if enabled
  const spouseRebateAmount = values.hasSpouseRebate ? calculateSpouseRebate(values.spouseRebate) : 0;
  const seniorsPensionerOffsetAmount = values.seniorsPensionerOffset ? 
    calculateSeniorsPensionerOffset(taxableIncome) : 0;

  // Total tax offsets
  const totalTaxOffsets = taxOffsets + dependantChildrenOffset + spouseRebateAmount + seniorsPensionerOffsetAmount;

  // Ensure taxableIncome is a valid number
  const safeTaxableIncome = isNaN(taxableIncome) || taxableIncome === null ? 0 : taxableIncome;

  // Core tax calculations with updated offsets - ensure all values are valid numbers
  const rawIncomeTax = calculateIncomeTax(safeTaxableIncome, taxYear, isResident, claimTaxFreeThreshold);
  const incomeTax = Math.max(0, (isNaN(rawIncomeTax) ? 0 : rawIncomeTax) - totalTaxOffsets);
  const medicareLevy = calculateMedicareLevy(safeTaxableIncome, medicareExempt) || 0;
  const medicareLevySurcharge = calculateMedicareLevySurcharge(safeTaxableIncome, hasPrivateHealth) || 0;
  const helpRepayment = calculateHELPRepayment(safeTaxableIncome, hasHELP) || 0;
  
  // Superannuation (mandatory + any additional contributions)
  let mandatorySuperannuation = 0;
  if (employmentType === "employee") {
    if (salaryIncludesSuper) {
      // If salary includes super, calculate the super component from the gross income
      mandatorySuperannuation = annualIncome - actualIncome;
      
      // Add super on bonus (which is always on top of the salary-included-super amount)
      if (bonusIncome > 0) {
        // When salary includes super, bonus should also be treated as including super
        mandatorySuperannuation += bonusIncome - actualBonusIncome;
      }
    } else {
      // Regular super calculation (extra on top of salary + bonus)
      // Calculate super on total income (regular + bonus)
      const totalIncomeAmount = actualIncome + (bonusIncome || 0);
      if (employmentType === "employee") {
        mandatorySuperannuation = totalIncomeAmount * effectiveSuperRate;
      }
    }
  }
  
  // Calculate base cap and total available cap with carry forward
  const baseCap = BASE_CONCESSIONAL_CAP;
  // Ensure carryForwardSuper is a number (not undefined or null)
  const safeCarryForwardSuper = typeof carryForwardSuper === 'number' ? carryForwardSuper : 0;
  const totalAvailableCap = baseCap + safeCarryForwardSuper;
  
  // Calculate how much of each component can be included under the cap
  // First, prioritize mandatory super as it can't be avoided
  const mandatorySuper = Math.min(mandatorySuperannuation, baseCap);
  let remainingBaseCap = baseCap - mandatorySuper;
  
  // Next, include salary sacrifice (which is explicitly chosen by the user)
  const salarySacrificeBelowBaseCap = Math.min(annualSalarySacrifice, remainingBaseCap);
  remainingBaseCap -= salarySacrificeBelowBaseCap;
  
  // If there's still salary sacrifice that exceeds the base cap, use carry forward cap
  const salarySacrificeUsingCarryForward = (remainingBaseCap <= 0) ? 
    Math.min(annualSalarySacrifice - salarySacrificeBelowBaseCap, safeCarryForwardSuper) : 0;
  
  // Calculate how much of the carry forward cap is remaining
  let remainingCarryForwardCap = safeCarryForwardSuper - salarySacrificeUsingCarryForward;
  
  // Finally, include voluntary super up to the remaining caps (base cap first, then carry forward)
  // Ensure voluntary super is a number
  const safeVoluntarySuper = typeof voluntarySuper === 'number' ? voluntarySuper : 0;
  const voluntarySuperBelowBaseCap = Math.min(safeVoluntarySuper, remainingBaseCap);
  const voluntarySuperUsingCarryForward = (remainingBaseCap < safeVoluntarySuper) ?
    Math.min(safeVoluntarySuper - voluntarySuperBelowBaseCap, remainingCarryForwardCap) : 0;
  
  // Sum the capped components to get the total concessional contributions
  // Only include contributions that fit within either the base cap or the total cap with carry forward
  const cappedConcessionalContributions = 
    mandatorySuper + 
    salarySacrificeBelowBaseCap + 
    salarySacrificeUsingCarryForward + 
    voluntarySuperBelowBaseCap + 
    voluntarySuperUsingCarryForward;
  
  // Non-concessional contributions (additional super) aren't subject to the cap
  // Ensure additionalSuper is a number
  const safeAdditionalSuper = typeof additionalSuper === 'number' ? additionalSuper : 0;
  const totalSuperannuation = cappedConcessionalContributions + safeAdditionalSuper;
  
  // Calculate Division 293 tax if applicable
  const division293Tax = calculateDivision293Tax(taxableIncome, totalSuperannuation);
  
  // Recalculate total tax with Division 293 included - make sure we handle any potential NaN/null
  const safeIncomeTax = isNaN(incomeTax) || incomeTax === null ? 0 : incomeTax;
  const safeMedicareLevy = isNaN(medicareLevy) || medicareLevy === null ? 0 : medicareLevy;
  const safeMedicareLevySurcharge = isNaN(medicareLevySurcharge) || medicareLevySurcharge === null ? 0 : medicareLevySurcharge;
  const safeHelpRepayment = isNaN(helpRepayment) || helpRepayment === null ? 0 : helpRepayment;
  const safeDivision293Tax = isNaN(division293Tax) || division293Tax === null ? 0 : division293Tax;
  const safeIncomeLevies = isNaN(incomeLevies) || incomeLevies === null ? 0 : incomeLevies;
  
  const updatedTotalTax = round(
    safeIncomeTax + 
    safeMedicareLevy + 
    safeMedicareLevySurcharge + 
    safeHelpRepayment + 
    safeDivision293Tax + 
    safeIncomeLevies
  );
  
  // Recalculate take home pay using the safe values
  const updatedTakeHomePay = round(safeTaxableIncome - updatedTotalTax);
  const updatedWeeklyTakeHome = round(updatedTakeHomePay / 52);
  const updatedFortnightlyTakeHome = round(updatedTakeHomePay / 26);
  const updatedMonthlyTakeHome = round(updatedTakeHomePay / 12);
  
  // Tax rates
  const effectiveTaxRate = taxableIncome > 0 ? round((updatedTotalTax / taxableIncome) * 100, 1) : 0;
  const marginalTaxRate = calculateMarginalTaxRate(taxableIncome, taxYear, isResident);
  
  // Calculate estimated tax return/owing
  // Positive value = tax refund, negative value = more tax to pay
  // Note: We exclude bonus income from PAYG withholding calculation as bonuses typically
  // don't have tax withheld at the correct marginal rate, resulting in tax owing later
  const estimatedTaxReturn = calculateEstimatedTaxReturn(
    updatedTotalTax,
    actualIncome, // Use base income (excluding bonus) for withholding calculation
    values.frequency,
    isResident,
    claimTaxFreeThreshold,
    hasHELP,
    !isResident && values.isResident === false // Special case for working holiday makers
  );
  
  // If there's bonus income, adjust the estimated tax return to account for additional tax on bonus
  // Since bonuses often have insufficient tax withheld or no withholding at all
  let adjustedEstimatedTaxReturn = estimatedTaxReturn;
  if (bonusIncome > 0) {
    // Estimate the additional tax that would be due on the bonus at the marginal rate
    const marginalRate = calculateMarginalTaxRate(taxableIncome, taxYear, isResident) / 100;
    // Estimate actual tax on bonus (simplified - just apply marginal rate to bonus amount)
    const estimatedBonusTax = bonusIncome * marginalRate;
    // Reduce the refund (or increase amount owing) by the estimated bonus tax
    // since this tax is typically not withheld properly
    adjustedEstimatedTaxReturn = estimatedTaxReturn - estimatedBonusTax;
  }
  
  return {
    inputIncome: annualIncome,            // Raw input converted to annual
    baseIncome: actualIncome,             // Regular income excluding bonus, adjusted for super
    bonusIncome: actualBonusIncome,       // Bonus component, adjusted for super
    grossIncome: totalIncome,             // Base + bonus, adjusted for super
    taxableIncome: taxableIncome,         // After deductions, what tax is calculated on
    incomeTax: round(incomeTax),
    medicareLevy: round(medicareLevy),
    medicareLevySurcharge: round(medicareLevySurcharge),
    helpRepayment: round(helpRepayment),
    division293Tax: round(division293Tax),
    totalTax: updatedTotalTax,
    takeHomePay: updatedTakeHomePay,
    weeklyTakeHome: updatedWeeklyTakeHome,
    fortnightlyTakeHome: updatedFortnightlyTakeHome,
    monthlyTakeHome: updatedMonthlyTakeHome,
    superannuation: round(totalSuperannuation),
    effectiveTaxRate,
    marginalTaxRate,
    estimatedTaxReturn: round(adjustedEstimatedTaxReturn)
  };
}
